import { produce } from "immer";
import { get, set } from "lodash-es";
import { useShallow } from "zustand/react/shallow";
import { initialLeitenLoading } from "../interfaces/ILeitenLoading";
import { useLeitenRequestStore } from "../stores/useLeitenRequestStore";
import { leitenRequest, resettableStoreSubscription } from "./leitenRequest";
/**
 * Represents a group request function that can handle multiple requests simultaneously.
 *
 * @template Store - The type of the store object.
 * @template P - The type of the path parameter.
 * @template Payload - The type of the payload.
 * @template Result - The type of the result.
 *
 * @param {StoreApi<Store>} store - The store object.
 * @param {P extends string
 *   ? Result extends void
 *     ? P
 *     : DotNestedValue<Store, P> extends Record<string, Result> | Array<any>
 *       ? P
 *       : never
 *   : never} path - The path parameter.
 * @param {(params: ILeitenGroupRequestParams<Payload>, extraArgument?: IExtraArgument) => Promise<Result>} payloadCreator - The function that creates the payload for the request.
 * @param {DotNestedValue<Store, P> extends Record<string, AcceptableType<Store>>
 *   ? ILeitenGroupRequestOption<Payload, Result>
 *   : ILeitenGroupRequestArrayOption<Payload, Result>} options - The options for the group request.
 *
 * @returns {ILeitenGroupRequest<Payload, Result>} - The group request function.
 */
export const leitenGroupRequest = (store, path, payloadCreator, options) => {
    const initialRequestState = initialLeitenLoading(options?.initialStatus);
    let requests = {};
    const isArray = Array.isArray(get(store.getState(), path));
    const getPathToArrayItem = (key) => {
        const raw = get(store.getState(), path, []);
        const source = Array.isArray(raw) ? raw : [];
        const find = source.findIndex((s) => options?.getKey?.(s) === key);
        const index = find !== -1 ? find : source.length;
        const withKey = (path + `["${index}"]`);
        return { withKey, isNew: find === -1 };
    };
    const add = (key) => {
        let pathWithKey = "";
        let payload = payloadCreator;
        if (isArray) {
            const before = getPathToArrayItem(key);
            pathWithKey = before.withKey;
            // eslint-disable-next-line
            // @ts-ignore
            payload = async (params, extraArgument) => {
                const result = await payloadCreator(params, extraArgument);
                const after = getPathToArrayItem(key);
                if ((before.isNew && after.isNew) || !after.isNew) {
                    const nextState = produce(store.getState(), (draft) => {
                        set(draft, after.withKey, result);
                    });
                    store.setState(nextState);
                }
            };
        }
        else {
            pathWithKey = (path + `.${key}`);
            if (options?.initialContent) {
                const initial = checkInitial(options.initialContent)
                    ? options.initialContent(key)
                    : options.initialContent;
                const nextState = produce(store.getState(), (draft) => {
                    set(draft, pathWithKey, initial);
                });
                store.setState(nextState);
            }
        }
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        requests[key] = leitenRequest(store, pathWithKey, payload, options);
    };
    const action = (params, options) => {
        params.forEach(({ key, params }) => {
            const request = requests[key];
            const payload = { key, params };
            if (request && !isArray) {
                request.action(payload, options);
            }
            else {
                add(key);
                requests[key].action(payload);
            }
        });
    };
    const clear = (key) => {
        if (key) {
            !isArray && requests[key].clear();
            delete requests[key];
        }
        else {
            const nextState = produce(store.getState(), (draft) => {
                set(draft, path, {});
            });
            store.setState(nextState);
            requests = {};
        }
    };
    const useRequest = (key, selector) => {
        return useLeitenRequestStore((state) => {
            const id = requests[key]?.key;
            return (selector || nonTypedReturn)((id && state[id]) || initialRequestState);
        });
    };
    const useGroupRequest = (selector) => {
        return useLeitenRequestStore(useShallow((state) => {
            const keys = Object.entries(requests).map(([id, value]) => ({
                id,
                key: value.key,
            }));
            const requestsStore = {};
            keys.forEach(({ id, key }) => {
                requestsStore[id] = state[key];
            });
            return (selector || nonTypedReturn)(requestsStore);
        }));
    };
    function hook(first, second) {
        if (first !== undefined && typeof first === "string") {
            return useRequest(first, second);
        }
        else {
            return useGroupRequest(first);
        }
    }
    resettableStoreSubscription(store, () => clear());
    return Object.assign(hook, { clear, action, requests, call: action });
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const nonTypedReturn = (value) => value;
const checkInitial = (value) => typeof value === "function";
// ------------------example;
// type IParams = { searchString: string };
// type IResult = { token: string };
// export const useAuthStore = create(() => ({
//   group: { new: {} as Record<string, IResult> },
//   value: "",
//   info: {
//     record: [] as IResult[],
//   },
//   record: [] as IParams[],
//   test: [] as string[],
// }));
//
// const request = async (params: ILeitenGroupRequestParams<IParams>): Promise<IResult> => {
//   return new Promise((resolve) => {
//     setTimeout(() => {
//       resolve({ token: "new token:" + params.key });
//       // resolve();
//     }, 2000);
//   });
// };
//
// const useStoreController = leitenGroupRequest(useAuthStore, "group.new", request, {
//   // optimisticUpdate: () => {
//   //   return { token: "new token:" };
//   // },
//   // getKey: (item) => item.token,
// });
//
// const testContent = useStoreController("test", (state) => state);
// const testContent1 = useStoreController((state) => state["12"].payload);
// const searchString = useStoreController("test", (state) => state.payload?.params.searchString);
// console.log(testContent, testContent1);
// console.log(searchString);
// useStoreController.call([{ key: "test", params: { searchString: "nothing" } }]);
// const searchString2 = useAuthStore.getState().group.new["12"];
